import {
  forwardRef,
  MouseEvent as ReactMouseEvent,
  type PropsWithChildren,
  type ReactElement,
} from 'react';

import { Menu } from '../../../../navigation/menu/Menu.js';
import { useCustomSeriesActions } from '../../hooks/useCustomSeriesActions.js';
import { renderMenuActionChildren } from '../../utils/render-menu-action-children.js';

export interface ChartActionsMenuProps {
  /** Controlled open or closed state */
  isOpen: boolean;
  /** Default actions to display **/
  defaultActions?: ReactElement<PropsWithChildren>;
  /** Custom series actions to display **/
  actions?:
    | ReactElement<PropsWithChildren>
    | Promise<ReactElement<PropsWithChildren>>;
  /** Handler for the action click */
  onActionClick?: (e: ReactMouseEvent) => void;
}

/**
 * ChartActionsMenu component.
 */
export const ChartActionsMenu = forwardRef<
  HTMLDivElement,
  PropsWithChildren<ChartActionsMenuProps>
>((props: PropsWithChildren<ChartActionsMenuProps>, forwardedRef) => {
  const { children, isOpen, actions, defaultActions, onActionClick } = props;
  const customActions = useCustomSeriesActions(actions, isOpen);

  const handleClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    const isSubmenuTrigger = (e.target as HTMLElement).closest(
      '[aria-haspopup="true"]',
    );
    if (!isSubmenuTrigger) {
      onActionClick?.(e);
    }
  };

  return (
    <div role="none" onClick={handleClick}>
      <Menu open={isOpen} modal={false}>
        <Menu.Trigger>{children}</Menu.Trigger>
        {isOpen && (
          <Menu.Content ref={forwardedRef} side="right">
            {defaultActions ? renderMenuActionChildren(defaultActions) : null}
            {customActions ? (
              <div data-dt-children-name="TooltipSeriesItems">
                {renderMenuActionChildren(customActions.props.children)}
              </div>
            ) : null}
          </Menu.Content>
        )}
      </Menu>
    </div>
  );
});

(
  ChartActionsMenu as typeof ChartActionsMenu & { displayName: string }
).displayName = 'ChartActionsMenu';
